/**
 * People IQ — Org Directory.
 * Card-based searchable employee directory. Uses server-side masking so
 * sensitive fields stay hidden for non-privileged viewers automatically.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Mail, Phone, Building2, UsersRound, ChevronRight } from "lucide-react";

export default function OrgDirectory() {
  const { user } = useAuth();
  const canSeeSensitive = hasCapability(user, "view_employee_sensitive");
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { lifecycle_state: undefined };
      if (dept !== "all") params.department_id = dept;
      const [{ data: emps }, { data: ds }, { data: gs }] = await Promise.all([
        api.get("/people/employees", { params }),
        api.get("/people/org/departments"),
        api.get("/people/org/designations"),
      ]);
      // Directory hides exited employees by default.
      setRows(emps.filter((r) => !["resigned", "terminated", "archived"].includes(r.lifecycle_state)));
      setDepts(ds);
      setDesigs(gs);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load directory");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept]);

  const deptMap = useMemo(() => Object.fromEntries(depts.map((d) => [d.id, d.name])), [depts]);
  const desigMap = useMemo(() => Object.fromEntries(desigs.map((d) => [d.id, d.name])), [desigs]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const rx = q.toLowerCase();
    return rows.filter((r) =>
      (r.full_name || "").toLowerCase().includes(rx) ||
      (r.employee_code || "").toString().includes(rx) ||
      (r.official_email || "").toLowerCase().includes(rx) ||
      (r.personal_email || "").toLowerCase().includes(rx) ||
      (r.official_mobile || "").includes(rx) ||
      (r.personal_mobile || "").includes(rx) ||
      (desigMap[r.designation_id] || "").toLowerCase().includes(rx),
    );
  }, [q, rows, desigMap]);

  return (
    <div className="space-y-6" data-testid="org-directory-page">
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Org Directory</h1>
        <p className="text-slate-500 mt-1 text-sm">{filtered.length} active employee(s) · {depts.length} departments</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-lg">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="dir-search"
                placeholder="Search by name, code, email, phone, designation…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-56" data-testid="dir-dept-filter"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {!canSeeSensitive && (
              <span className="text-xs text-slate-400 italic">Contact info masked for your role.</span>
            )}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center text-slate-400 text-sm py-16">Loading directory…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          <UsersRound className="w-10 h-10 mx-auto mb-2 opacity-40" /> No employees match.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="dir-grid">
          {filtered.map((r) => (
            <PersonCard key={r.id}
                        emp={r}
                        dept={deptMap[r.department_id]}
                        desig={desigMap[r.designation_id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PersonCard({ emp, dept, desig }) {
  const initials = (emp.full_name || "").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";
  const photoSrc = emp.photograph_storage_path
    ? `${api.defaults.baseURL}/people/employees/${emp.id}/photograph`
    : null;

  return (
    <Link
      to={`/people/${emp.id}`}
      className="group block rounded-lg border border-slate-200 bg-white hover:shadow-md hover:border-[#7C1D3A]/30 transition-all overflow-hidden"
      data-testid={`dir-card-${emp.employee_code}`}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center text-slate-500 font-heading font-bold shrink-0 ring-2 ring-white shadow-sm">
          {photoSrc ? (
            <img src={photoSrc} alt={emp.full_name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-slate-900 font-semibold truncate group-hover:text-[#7C1D3A]">
            {emp.full_name}
            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="text-xs text-slate-500 truncate">{desig || "—"}</div>
          <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5 truncate">
            <Building2 className="w-3 h-3" /> {dept || "—"}
          </div>
        </div>
        <Badge variant="outline" className="font-mono text-[10px] shrink-0">{emp.employee_code}</Badge>
      </div>
      <div className="px-4 pb-3 space-y-1 text-xs text-slate-600 border-t border-slate-100 pt-2">
        {(emp.official_email || emp.personal_email) && (
          <div className="flex items-center gap-1.5 truncate">
            <Mail className="w-3 h-3 text-slate-400 shrink-0" />
            <span className="truncate">{emp.official_email || emp.personal_email}</span>
          </div>
        )}
        {(emp.official_mobile || emp.personal_mobile) && (
          <div className="flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-slate-400" /> {emp.official_mobile || emp.personal_mobile}
          </div>
        )}
      </div>
    </Link>
  );
}
